import { useState, useRef, useCallback, useEffect } from "react";
import { Room, RoomEvent, Track, ConnectionState, ConnectionQuality, VideoQuality, } from "livekit-client";
import { MAX_FILE_SHARE_BYTES } from "../utils/callFileShare";
import { createLiveKitFileReceiver, sendFileOverLiveKit, FILE_SHARE_TOPIC } from "../utils/livekitFileShare";
import { getLiveKitToken, CONNECT_FAILED_ERROR, CONNECT_TIMEOUT_ERROR, getSfuCategory, categorizedError } from "../utils/livekitApi";
import { createCallE2EE, setCallE2EEKey, callE2EESupported, E2EE_KEY_UNAVAILABLE_ERROR, E2EE_UNSUPPORTED_ERROR, E2EE_SETUP_FAILED_ERROR } from "../utils/callE2EE";
import { useBackgroundedTracker } from "./useBackgroundedTracker";
/** Mobile devices get a lower capture ceiling — encoding is the main battery/heat drain. */
function isMobileDevice() {
    return (typeof navigator !== "undefined" &&
        (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)));
}
/** A LiveKit publication may remain in a participant's map for a short time
 * after its underlying browser track has ended. Never prefer that stale
 * screen publication over a still-live camera publication. */
function hasLiveMediaTrack(track) {
    return !!track?.mediaStreamTrack && track.mediaStreamTrack.readyState !== "ended";
}
/** While in-call, rebroadcast presence this often (ongoing-call discovery). */
// Sentinel error message → the UI shows a "camera/mic permission" banner
// instead of the generic SFU-unreachable text.
export const MEDIA_DENIED_ERROR = "media-permission-denied";
/** Sentinel shown when the automatic TURN/relay rescue could not connect. */
export const RELAY_RECOVERY_FAILED_ERROR = "relay-recovery-failed";
const BEACON_INTERVAL_MS = 8_000;
const ONGOING_TTL_MS = 20_000;
const ONGOING_CHECK_MS = 5_000;
/** Timeout for the LiveKit room.connect() call (WS + ICE). */
const CONNECT_TIMEOUT_MS = 15_000;
/** Ignore a single brief POOR sample, but rescue sustained unusable media fast. */
const POOR_QUALITY_GRACE_MS = 2_500;
/** Media reconnecting already means the user has lost media; react sooner. */
const MEDIA_RECONNECT_GRACE_MS = 750;
/** Keep the successful relay notice visible briefly, then get out of the way. */
const RELAY_NOTICE_MS = 6_000;
/** Thermal-safe camera ceiling latched after the local mobile connection becomes unusable. */
const MOBILE_THERMAL_CAPTURE = { width: 320, height: 180, frameRate: 15 };
/**
 * Ask for browser media permissions directly from the user's click, before
 * token fetching and LiveKit connection awaits can consume iOS Safari's
 * transient user activation. LiveKit opens the real capture tracks after the
 * room connects; these short-lived preflight tracks only unlock permission.
 */
async function preflightBrowserMediaPermissions(video, facingMode) {
    const getUserMedia = globalThis.navigator?.mediaDevices?.getUserMedia;
    if (!getUserMedia)
        return;
    let microphoneStream = null;
    try {
        microphoneStream = await getUserMedia.call(navigator.mediaDevices, {
            audio: true,
            video: false,
        });
    }
    catch (err) {
        console.warn("[lkCall] microphone permission preflight failed:", err);
        throw new Error(MEDIA_DENIED_ERROR);
    }
    finally {
        microphoneStream?.getTracks().forEach((track) => track.stop());
    }
    if (!video)
        return;
    let cameraStream = null;
    try {
        cameraStream = await getUserMedia.call(navigator.mediaDevices, {
            audio: false,
            video: { facingMode },
        });
    }
    catch (err) {
        // Camera remains optional. LiveKit will make the same best-effort attempt
        // after connecting and keep the participant audio-only if it is denied.
        console.warn("[lkCall] camera permission preflight failed — continuing audio-only:", err);
    }
    finally {
        cameraStream?.getTracks().forEach((track) => track.stop());
    }
}
/**
 * Before an automatic transport rebuild, avoid any path that could reopen a
 * browser permission prompt without a user click. Browsers that do not expose
 * microphone through Permissions API can safely reuse the permission proven
 * by the explicit preflight that started the current call.
 */
async function browserMicrophonePermissionIsReusable() {
    const query = globalThis.navigator?.permissions?.query;
    if (!query)
        return true;
    try {
        const status = await query.call(navigator.permissions, { name: "microphone" });
        return status.state === "granted";
    }
    catch {
        // Safari versions without microphone PermissionDescriptor support throw.
        // The successful explicit preflight remains our proof in that case.
        return true;
    }
}
function stopLiveKitTrackImmediately(track) {
    const mediaTrack = track?.mediaStreamTrack;
    if (typeof mediaTrack?.stop === "function")
        mediaTrack.stop();
    if (typeof track?.stop === "function")
        track.stop();
}
const LIVEKIT_REJOIN_STORAGE_KEY = "dw-livekit-call-rejoin";
function saveStoredRejoin(roomName, info) {
    if (!roomName || !info)
        return;
    try {
        const entry = { roomName, ...info };
        sessionStorage.setItem(LIVEKIT_REJOIN_STORAGE_KEY, JSON.stringify(entry));
    }
    catch {
        /* storage unavailable — rejoin remains available until this hook unmounts */
    }
}
function loadStoredRejoin(roomName) {
    if (!roomName)
        return null;
    try {
        const raw = sessionStorage.getItem(LIVEKIT_REJOIN_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (parsed.roomName !== roomName || typeof parsed.callId !== "string")
            return null;
        return {
            callId: parsed.callId,
            video: !!parsed.video,
            ts: typeof parsed.ts === "number" ? parsed.ts : Date.now(),
            forceRelay: !!parsed.forceRelay,
            announceOnConnect: !!parsed.announceOnConnect,
            muted: !!parsed.muted,
            mutedByHost: !!parsed.mutedByHost,
            cameraOff: !!parsed.cameraOff,
        };
    }
    catch {
        return null;
    }
}
function clearStoredRejoin() {
    try {
        sessionStorage.removeItem(LIVEKIT_REJOIN_STORAGE_KEY);
    }
    catch {
        /* ignore unavailable storage */
    }
}
function genCallId() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto)
        return `lk-${crypto.randomUUID()}`;
    return `lk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
export function useLiveKitCall({ deviceId, isConnected, sendCallSignal, canReceiveFile, roomName, guestToken, deviceSessionToken, signWalletMessage, getE2EEKeyMaterial, e2eeKeyAvailable, enabled, }) {
    const wasRecentlyBackgrounded = useBackgroundedTracker();
    const [callState, setCallState] = useState("idle");
    const [connectionPhase, setConnectionPhase] = useState("dialing");
    const [participants, setParticipants] = useState([]);
    const [incomingFrom, setIncomingFrom] = useState(null);
    const [callId, setCallId] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    /** Host toggle state: true after mute-all until the host withdraws it. */
    const [muteAllActive, setMuteAllActive] = useState(false);
    /** Raise-hand: own hand + the set of participants with raised hands. */
    const [handRaised, setHandRaised] = useState(false);
    const handRaisedRef = useRef(false);
    const [raisedHands, setRaisedHands] = useState([]);
    const [isVideoCall, setIsVideoCall] = useState(false);
    const [incomingVideo, setIncomingVideo] = useState(false);
    const [isCameraOff, setIsCameraOff] = useState(false);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [screenShareOwnerId, setScreenShareOwnerId] = useState(null);
    const [screenShareClaimPending, setScreenShareClaimPending] = useState(false);
    const [localStream, setLocalStream] = useState(null);
    const [canRejoin, setCanRejoin] = useState(false);
    const [ongoingCall, setOngoingCall] = useState(null);
    /** Identities LiveKit currently reports as speaking (for tile highlight). */
    const activeSpeakersRef = useRef(new Set());
    const [callDropped, setCallDropped] = useState(false);
    /** True when the drop was (very likely) caused by the user backgrounding
     *  the app — the UI shows a "you left the app" notice instead of the
     *  generic connection error. */
    const [callDroppedByBackground, setCallDroppedByBackground] = useState(false);
    const [fileTransfers, setFileTransfers] = useState([]);
    /** User-facing error when the SFU is unreachable / token denied. */
    const [callError, setCallError] = useState(null);
    /** Automatic bad-network rescue status exposed to the call panel. */
    const [connectionRecovery, setConnectionRecovery] = useState(null);
    /** null = not in call / unknown; true = all participants frame-E2EE;
     *  'partial' = some participants unencrypted (mixed room);
     *  false = joined without E2EE (unsupported browser). */
    const [e2eeActive, setE2eeActive] = useState(null);
    const e2eeWorkerRef = useRef(null);
    const e2eeLocalRef = useRef(false);
    /** Provider of the current room, kept for the retroactive key upgrade. */
    const e2eeKeyProviderRef = useRef(null);
    /** True while the room runs WITHOUT E2EE but could still be upgraded
     *  (browser supports it, key just hasn't arrived yet). */
    const e2eePendingKeyRef = useRef(false);
    /** Guards against concurrent upgrade attempts. */
    const e2eeUpgradeInFlightRef = useRef(false);
    /** Recompute call-level E2EE state: only 'true' when every participant is encrypted. */
    const updateE2eeStatus = useCallback((room) => {
        if (!e2eeLocalRef.current) {
            setE2eeActive(false);
            return;
        }
        let allEncrypted = true;
        room.remoteParticipants.forEach((p) => {
            if (!p.isEncrypted)
                allEncrypted = false;
        });
        setE2eeActive(allEncrypted ? true : "partial");
        if (room.state === ConnectionState.Connected) {
            setConnectionPhase(allEncrypted ? "secure" : "encrypting");
        }
    }, []);
    const roomRef = useRef(null);
    /** Room identity that the currently connected room actually belongs to.
     *  Unlike the prop, this cannot change before the old room is torn down. */
    const activeRoomNameRef = useRef(null);
    const previousRoomNameRef = useRef(roomName);
    const callStateRef = useRef("idle");
    const callIdRef = useRef(null);
    const isVideoCallRef = useRef(false);
    const incomingVideoRef = useRef(false);
    const isMutedRef = useRef(false);
    // True while our mic is muted BECAUSE the host asked (lk-mute-request).
    // Only such a mute may be undone remotely by the host's unmute request —
    // a manual mute (or manual re-mute after the request) stays private.
    const mutedByHostRef = useRef(false);
    const isCameraOffRef = useRef(false);
    const isScreenSharingRef = useRef(false);
    const deviceIdRef = useRef(deviceId);
    const screenShareOwnerRef = useRef(null);
    const screenShareOwnerByCallRef = useRef(new Map());
    const syncParticipantsRef = useRef(() => { });
    const screenShareClaimPendingRef = useRef(false);
    const screenShareClaimTimerRef = useRef(null);
    const startScreenShareAfterClaimRef = useRef(null);
    /** Serializes screen-share start/stop transitions; prevents concurrent
     *  async setScreenShareEnabled calls from racing each other. */
    const screenShareTransitionRef = useRef(false);
    /** Browser/OS stop can arrive while capture startup still owns the
     * transition lock. Replay that stop as soon as startup releases it. */
    const screenShareEndedDuringTransitionRef = useRef(false);
    const screenShareStopInProgressRef = useRef(false);
    const screenShareGenerationRef = useRef(0);
    const screenShareTransitionOperationRef = useRef(0);
    const stopLocalScreenShareRef = useRef(null);
    const facingRef = useRef("user");
    /** The most recent user-selected facing, including while a restart is queued. */
    const desiredFacingRef = useRef("user");
    const sendRef = useRef(sendCallSignal);
    const canReceiveFileRef = useRef(canReceiveFile);
    const beaconTimerRef = useRef(null);
    const ongoingSeenAtRef = useRef(0);
    const rejoinInfoRef = useRef(null);
    const sendingFileRef = useRef(false);
    const connectSeqRef = useRef(0);
    /** Set only by a click-driven media preflight in this page lifecycle. */
    const explicitMediaPermissionRef = useRef(false);
    /** True only for the current room after a successful relay-only connect. */
    const relayModeRef = useRef(false);
    /** One rescue at a time; also prevents a poor relay from causing a loop. */
    const recoveryInFlightRef = useRef(false);
    /** Invalidates an in-flight rescue when teardown/unmount/manual leave wins. */
    const recoveryGenerationRef = useRef(0);
    const recoveryTimerRef = useRef(null);
    const recoveryNoticeTimerRef = useRef(null);
    const recoveryHandlerRef = useRef(() => { });
    /** Kept across relay rebuild/rejoin for the same call id, reset only for a
     *  distinct call (or a final teardown). */
    const thermalCeilingCallIdRef = useRef(null);
    const mobileThermalCeilingRef = useRef(false);
    /** Invalidates camera work owned by a disposed room/call lifecycle. */
    const cameraRestartGenerationRef = useRef(0);
    const cameraRestartRevisionRef = useRef(0);
    const cameraRestartQueueRef = useRef(Promise.resolve());
    deviceIdRef.current = deviceId;
    sendRef.current = sendCallSignal;
    canReceiveFileRef.current = canReceiveFile;
    const getE2EEKeyMaterialRef = useRef(getE2EEKeyMaterial);
    getE2EEKeyMaterialRef.current = getE2EEKeyMaterial;
    const setStateBoth = (s) => {
        callStateRef.current = s;
        setCallState(s);
    };
    // Read through a function after async boundaries so TypeScript does not
    // incorrectly keep the pre-await state narrowing from the entry guard.
    const currentCallState = () => callStateRef.current;
    const setCallIdBoth = (id) => {
        callIdRef.current = id;
        setCallId(id);
        const owner = id ? (screenShareOwnerByCallRef.current.get(id) ?? null) : null;
        screenShareOwnerRef.current = owner;
        setScreenShareOwnerId(owner);
        syncParticipantsRef.current();
    };
    const setVideoBoth = (v) => {
        isVideoCallRef.current = v;
        setIsVideoCall(v);
    };
    const clearPendingRecovery = useCallback(() => {
        if (recoveryTimerRef.current) {
            clearTimeout(recoveryTimerRef.current);
            recoveryTimerRef.current = null;
        }
    }, []);
    const clearRecoveryTimers = useCallback(() => {
        clearPendingRecovery();
        if (recoveryNoticeTimerRef.current) {
            clearTimeout(recoveryNoticeTimerRef.current);
            recoveryNoticeTimerRef.current = null;
        }
    }, [clearPendingRecovery]);
    const upsertTransfer = useCallback((tr) => {
        setFileTransfers((prev) => {
            const idx = prev.findIndex((p) => p.id === tr.id && p.direction === tr.direction);
            if (idx === -1)
                return [...prev, tr];
            const merged = { ...prev[idx], ...tr, blobUrl: tr.blobUrl || prev[idx].blobUrl };
            const next = prev.slice();
            next[idx] = merged;
            return next;
        });
    }, []);
    const fileReceiverRef = useRef(createLiveKitFileReceiver(upsertTransfer));
    /**
     * Request a lower simulcast layer for remote camera videos in large calls.
     * Receiver decode is the bottleneck (adaptiveStream is off, so without this
     * every client pulls 720p from everyone): with 5-6 participants total we
     * ask for MEDIUM, with 7+ for LOW; below 5 we go back to HIGH (also when
     * people leave). Screen shares stay at HIGH — text must remain readable.
     */
    const applyRemoteVideoQuality = useCallback(() => {
        const room = roomRef.current;
        if (!room)
            return;
        const total = room.remoteParticipants.size + 1; // include ourselves
        const quality = isMobileDevice() && mobileThermalCeilingRef.current
            ? VideoQuality.LOW
            : total >= 7
                ? VideoQuality.LOW
                : total >= 5
                    ? VideoQuality.MEDIUM
                    : VideoQuality.HIGH;
        room.remoteParticipants.forEach((p) => {
            const camPub = p.getTrackPublication(Track.Source.Camera);
            if (camPub && camPub.isSubscribed) {
                try {
                    camPub.setVideoQuality(quality);
                }
                catch (err) {
                    console.warn("[lkCall] setVideoQuality failed:", err);
                }
            }
        });
    }, []);
    /** Permanently lower camera work for this mobile call. Screen share uses a
     *  separate publication/source and deliberately remains unconstrained. */
    const restartLocalCamera = useCallback((room, facingMode) => {
        const generation = cameraRestartGenerationRef.current;
        const revision = ++cameraRestartRevisionRef.current;
        const restart = async () => {
            if (generation !== cameraRestartGenerationRef.current ||
                roomRef.current !== room) {
                return false;
            }
            const camera = room.localParticipant.getTrackPublication(Track.Source.Camera)
                ?.track;
            if (!camera)
                return false;
            await camera.restartTrack({
                facingMode,
                ...(isMobileDevice() && mobileThermalCeilingRef.current
                    ? { resolution: MOBILE_THERMAL_CAPTURE }
                    : {}),
            });
            if (generation !== cameraRestartGenerationRef.current ||
                roomRef.current !== room ||
                revision !== cameraRestartRevisionRef.current) {
                return false;
            }
            facingRef.current = facingMode;
            return true;
        };
        const result = cameraRestartQueueRef.current.then(restart, restart);
        cameraRestartQueueRef.current = result.then(() => { }, () => { });
        return result;
    }, []);
    const latchMobileThermalCeiling = useCallback((room) => {
        if (!isMobileDevice() || mobileThermalCeilingRef.current)
            return;
        mobileThermalCeilingRef.current = true;
        applyRemoteVideoQuality();
        void restartLocalCamera(room, desiredFacingRef.current)
            .catch((err) => console.warn("[lkCall] thermal camera downscale failed:", err));
    }, [applyRemoteVideoQuality, restartLocalCamera]);
    /** Rebuild the participants array from the room's current remote state. */
    const syncParticipants = useCallback(() => {
        const room = roomRef.current;
        if (!room) {
            setParticipants([]);
            return;
        }
        const list = [];
        room.remoteParticipants.forEach((p) => {
            const stream = new MediaStream();
            const screenPub = p.getTrackPublication(Track.Source.ScreenShare);
            const camPub = p.getTrackPublication(Track.Source.Camera);
            const micPub = p.getTrackPublication(Track.Source.Microphone);
            const liveScreenTrack = screenPub?.isSubscribed && !screenPub.isMuted && hasLiveMediaTrack(screenPub.track)
                ? screenPub.track
                : null;
            const liveCameraTrack = camPub?.isSubscribed && !camPub.isMuted && hasLiveMediaTrack(camPub.track)
                ? camPub.track
                : null;
            const videoTrack = liveScreenTrack || liveCameraTrack;
            if (videoTrack?.mediaStreamTrack)
                stream.addTrack(videoTrack.mediaStreamTrack);
            if (micPub?.isSubscribed && micPub.track?.mediaStreamTrack) {
                stream.addTrack(micPub.track.mediaStreamTrack);
            }
            list.push({
                deviceId: p.identity,
                stream: stream.getTracks().length > 0 ? stream : null,
                isScreenSharing: screenShareOwnerRef.current === p.identity &&
                    !!liveScreenTrack,
                cameraOff: !liveScreenTrack && !liveCameraTrack,
                micMuted: !micPub || micPub.isMuted,
                speaking: activeSpeakersRef.current.has(p.identity) || p.isSpeaking,
            });
        });
        setParticipants(list);
    }, []);
    syncParticipantsRef.current = syncParticipants;
    const syncLocalStream = useCallback(() => {
        const room = roomRef.current;
        if (!room) {
            setLocalStream(null);
            if (isScreenSharingRef.current) {
                isScreenSharingRef.current = false;
                setIsScreenSharing(false);
            }
            return;
        }
        const stream = new MediaStream();
        const lp = room.localParticipant;
        const screenPub = lp.getTrackPublication(Track.Source.ScreenShare);
        const cameraPub = lp.getTrackPublication(Track.Source.Camera);
        const screen = screenPub?.track;
        const cam = cameraPub?.track;
        const mic = lp.getTrackPublication(Track.Source.Microphone)?.track;
        const liveScreen = !screenPub?.isMuted && hasLiveMediaTrack(screen) ? screen : null;
        const liveCamera = !cameraPub?.isMuted && hasLiveMediaTrack(cam) ? cam : null;
        const actuallySharing = !!liveScreen;
        if (isScreenSharingRef.current &&
            !actuallySharing &&
            screenShareOwnerRef.current === deviceIdRef.current &&
            callIdRef.current) {
            sendRef.current({ subtype: "lk-screen-share-release", callId: callIdRef.current });
            screenShareOwnerByCallRef.current.set(callIdRef.current, null);
            screenShareOwnerRef.current = null;
            setScreenShareOwnerId(null);
            syncParticipantsRef.current();
        }
        if (isScreenSharingRef.current !== actuallySharing) {
            isScreenSharingRef.current = actuallySharing;
            setIsScreenSharing(actuallySharing);
        }
        // The screen publication is the outgoing presentation only. Keep the
        // public localStream camera-only so the self-preview never renders shared
        // pixels (and remains available while both publications are live).
        if (liveCamera?.mediaStreamTrack)
            stream.addTrack(liveCamera.mediaStreamTrack);
        if (mic?.mediaStreamTrack)
            stream.addTrack(mic.mediaStreamTrack);
        setLocalStream(stream.getTracks().length > 0 ? stream : null);
    }, []);
    const teardown = useCallback((opts) => {
        const activeCallId = callIdRef.current;
        screenShareGenerationRef.current += 1;
        screenShareTransitionOperationRef.current += 1;
        screenShareTransitionRef.current = false;
        screenShareEndedDuringTransitionRef.current = false;
        screenShareStopInProgressRef.current = false;
        if (screenShareOwnerRef.current === deviceIdRef.current) {
            const activeShare = roomRef.current?.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track;
            stopLiveKitTrackImmediately(activeShare);
        }
        if (screenShareOwnerRef.current === deviceIdRef.current && activeCallId) {
            sendRef.current({ subtype: "lk-screen-share-release", callId: activeCallId });
            screenShareOwnerByCallRef.current.set(activeCallId, null);
        }
        if (screenShareClaimTimerRef.current) {
            clearTimeout(screenShareClaimTimerRef.current);
            screenShareClaimTimerRef.current = null;
        }
        screenShareClaimPendingRef.current = false;
        setScreenShareClaimPending(false);
        screenShareOwnerRef.current = null;
        setScreenShareOwnerId(null);
        connectSeqRef.current += 1; // invalidate any in-flight connect
        cameraRestartGenerationRef.current += 1;
        cameraRestartRevisionRef.current += 1;
        cameraRestartQueueRef.current = Promise.resolve();
        recoveryGenerationRef.current += 1;
        clearRecoveryTimers();
        recoveryInFlightRef.current = false;
        relayModeRef.current = false;
        setConnectionRecovery(null);
        if (beaconTimerRef.current) {
            clearInterval(beaconTimerRef.current);
            beaconTimerRef.current = null;
        }
        if (e2eeWorkerRef.current) {
            e2eeWorkerRef.current.terminate();
            e2eeWorkerRef.current = null;
        }
        setE2eeActive(null);
        setConnectionPhase("dialing");
        e2eeLocalRef.current = false;
        e2eeKeyProviderRef.current = null;
        e2eePendingKeyRef.current = false;
        e2eeUpgradeInFlightRef.current = false;
        const room = roomRef.current;
        roomRef.current = null;
        activeRoomNameRef.current = null;
        if (room) {
            room.removeAllListeners();
            // Explicitly stop every local capture track BEFORE the async
            // disconnect: on iOS Safari a mic/camera track that is merely
            // dereferenced (not stopped) can keep the capture pipeline running,
            // draining battery and heating the phone long after the call.
            try {
                for (const pub of room.localParticipant.trackPublications.values()) {
                    pub.track?.mediaStreamTrack?.stop();
                    pub.track?.stop();
                }
            }
            catch {
                /* best-effort — disconnect below is the primary cleanup */
            }
            room.disconnect().catch(() => { });
        }
        if (!opts?.keepRejoin) {
            rejoinInfoRef.current = null;
            clearStoredRejoin();
            thermalCeilingCallIdRef.current = null;
            mobileThermalCeilingRef.current = false;
        }
        else {
            saveStoredRejoin(roomName, rejoinInfoRef.current);
        }
        setCanRejoin(!!opts?.keepRejoin);
        setFileTransfers((prev) => {
            prev.forEach((tr) => {
                if (tr.blobUrl)
                    URL.revokeObjectURL(tr.blobUrl);
            });
            return [];
        });
        fileReceiverRef.current = createLiveKitFileReceiver(upsertTransfer);
        sendingFileRef.current = false;
        setParticipants([]);
        setLocalStream(null);
        setIncomingFrom(null);
        incomingVideoRef.current = false;
        setIncomingVideo(false);
        isMutedRef.current = false;
        mutedByHostRef.current = false;
        setIsMuted(false);
        setMuteAllActive(false);
        handRaisedRef.current = false;
        setHandRaised(false);
        setRaisedHands([]);
        isCameraOffRef.current = false;
        setIsCameraOff(false);
        isScreenSharingRef.current = false;
        setIsScreenSharing(false);
        facingRef.current = "user";
        desiredFacingRef.current = "user";
        cameraRestartRevisionRef.current += 1;
        setVideoBoth(false);
        setCallIdBoth(null);
        setStateBoth("idle");
    }, [upsertTransfer, clearRecoveryTimers, roomName]);
    /**
     * Queue a relay rescue for the current direct room. A timer makes POOR
     * samples noise-tolerant; fatal disconnects use delay=0. The actual recovery
     * callback lives in a ref because it calls connect(), which is declared below.
     */
    const scheduleRelayRecovery = useCallback((room, reason, delayMs) => {
        if (wasRecentlyBackgrounded()) {
            clearPendingRecovery();
            setConnectionRecovery(null);
            return;
        }
        if (roomRef.current !== room ||
            callStateRef.current === "idle" ||
            relayModeRef.current ||
            recoveryInFlightRef.current ||
            recoveryTimerRef.current) {
            return;
        }
        recoveryTimerRef.current = setTimeout(() => {
            recoveryTimerRef.current = null;
            if (wasRecentlyBackgrounded()) {
                setConnectionRecovery(null);
                return;
            }
            if (roomRef.current === room &&
                callStateRef.current !== "idle" &&
                !relayModeRef.current &&
                !recoveryInFlightRef.current) {
                recoveryHandlerRef.current(room, reason);
            }
        }, delayMs);
    }, [clearPendingRecovery, wasRecentlyBackgrounded]);
    /**
     * Dispose only the old LiveKit room while keeping call/UI intent intact.
     * This is deliberately separate from teardown(), which resets the call to
     * idle and would lose mute/camera/E2EE continuity during a relay rescue.
     */
    const disposeRoomForRecovery = useCallback(async (room) => {
        if (roomRef.current !== room)
            return false;
        screenShareGenerationRef.current += 1;
        screenShareTransitionOperationRef.current += 1;
        screenShareTransitionRef.current = false;
        screenShareEndedDuringTransitionRef.current = false;
        screenShareStopInProgressRef.current = false;
        if (screenShareOwnerRef.current === deviceIdRef.current && callIdRef.current) {
            const activeShare = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track;
            stopLiveKitTrackImmediately(activeShare);
            sendRef.current({ subtype: "lk-screen-share-release", callId: callIdRef.current });
            screenShareOwnerByCallRef.current.set(callIdRef.current, null);
        }
        screenShareOwnerRef.current = null;
        setScreenShareOwnerId(null);
        roomRef.current = null;
        cameraRestartGenerationRef.current += 1;
        cameraRestartRevisionRef.current += 1;
        cameraRestartQueueRef.current = Promise.resolve();
        room.removeAllListeners();
        if (beaconTimerRef.current) {
            clearInterval(beaconTimerRef.current);
            beaconTimerRef.current = null;
        }
        try {
            for (const pub of room.localParticipant.trackPublications.values()) {
                pub.track?.mediaStreamTrack?.stop();
                pub.track?.stop();
            }
        }
        catch {
            /* best-effort; disconnect below is the primary cleanup */
        }
        // Clear old-room-owned refs before awaiting disconnect. If the user
        // leaves and starts a new call while disconnect is slow, this cleanup
        // must not resume later and wipe the new room's worker or UI state.
        const oldWorker = e2eeWorkerRef.current;
        e2eeWorkerRef.current = null;
        oldWorker?.terminate();
        e2eeKeyProviderRef.current = null;
        e2eePendingKeyRef.current = false;
        e2eeUpgradeInFlightRef.current = false;
        e2eeLocalRef.current = false;
        setE2eeActive(null);
        activeSpeakersRef.current = new Set();
        setParticipants([]);
        setLocalStream(null);
        // Browser screen capture cannot be restarted without a fresh user
        // gesture. Stop advertising a stale share after the old room is gone.
        isScreenSharingRef.current = false;
        setIsScreenSharing(false);
        sendingFileRef.current = false;
        fileReceiverRef.current = createLiveKitFileReceiver(upsertTransfer);
        try {
            await room.disconnect();
        }
        catch {
            /* the broken direct room may already be disconnected */
        }
        return true;
    }, [upsertTransfer]);
    /** Connect to the LiveKit room and publish mic (+camera for video calls).
     *  Resolves true on success, false if the attempt was superseded/canceled. */
    const connect = useCallback(async (video, id, options = {}) => {
        if (!roomName)
            throw new Error("no room");
        if (thermalCeilingCallIdRef.current !== id) {
            thermalCeilingCallIdRef.current = id;
            mobileThermalCeilingRef.current = false;
        }
        const seq = ++connectSeqRef.current;
        setCallError(null);
        setConnectionPhase("dialing");
        if (options.isCancelled?.())
            return false;
        // The media-permission preflight is the dialing stage. Once the token
        // request starts, the guest JWT or wallet signature is actively being
        // verified by the orchestrator.
        setConnectionPhase("authenticating");
        const access = await getLiveKitToken({
            room: roomName,
            deviceId,
            guestToken,
            deviceSessionToken,
            signWalletMessage,
        });
        if (seq !== connectSeqRef.current || options.isCancelled?.())
            return false;
        // Authorization succeeded. From here until the encrypted room is
        // connected, the client is preparing and activating frame E2EE.
        setConnectionPhase("encrypting");
        // No Room/connect/publication until keyed encryption is ready.
        let e2ee;
        try {
            const getKey = getE2EEKeyMaterialRef.current;
            e2ee = await createCallE2EE(getKey ?? (async () => null));
        }
        catch (err) {
            if (seq !== connectSeqRef.current || options.isCancelled?.())
                return false;
            if (err instanceof Error && [E2EE_KEY_UNAVAILABLE_ERROR, E2EE_UNSUPPORTED_ERROR].includes(err.message))
                throw err;
            throw new Error(E2EE_SETUP_FAILED_ERROR);
        }
        if (seq !== connectSeqRef.current || options.isCancelled?.()) {
            if (e2ee)
                e2ee.worker.terminate();
            return false;
        }
        // NOTE: adaptiveStream must stay OFF — it sizes quality from elements
        // attached via track.attach(), but our UI renders raw MediaStreams, so
        // the SFU would assume a tiny/invisible view and send the lowest layer.
        // Simulcast + dynacast still adapt to each receiver's bandwidth.
        // Mobile: 640x360@24 capture ceiling — encoding 720p30 (plus simulcast
        // layers) is the main phone-heating/battery drain, and a phone screen
        // never shows more than ~360p per tile anyway.
        const captureRes = isMobileDevice()
            ? mobileThermalCeilingRef.current
                ? MOBILE_THERMAL_CAPTURE
                : { width: 640, height: 360, frameRate: 24 }
            : { width: 1280, height: 720, frameRate: 30 };
        const room = new Room({
            adaptiveStream: false,
            dynacast: true,
            videoCaptureDefaults: { resolution: captureRes },
            // dtx: silence costs ~0 bandwidth; red: redundant audio packets mask
            // packet loss on bad mobile links (audio stays intelligible).
            publishDefaults: { simulcast: true, dtx: true, red: true },
            ...(e2ee ? { e2ee: { keyProvider: e2ee.keyProvider, worker: e2ee.worker } } : {}),
        });
        e2eeLocalRef.current = false;
        e2eePendingKeyRef.current = false;
        if (e2ee?.hasKey) {
            try {
                await room.setE2EEEnabled(true);
                e2eeLocalRef.current = true;
            }
            catch (err) {
                e2ee.worker.terminate();
                await room.disconnect().catch(() => { });
                throw new Error(E2EE_SETUP_FAILED_ERROR);
            }
        }
        if (seq !== connectSeqRef.current || options.isCancelled?.()) {
            e2eeLocalRef.current = false;
            e2eePendingKeyRef.current = false;
            if (e2ee)
                e2ee.worker.terminate();
            return false;
        }
        updateE2eeStatus(room);
        const roomScreenShareGeneration = screenShareGenerationRef.current;
        room
            .on(RoomEvent.ParticipantConnected, () => {
            if (callStateRef.current === "calling")
                setStateBoth("in-call");
            syncParticipants();
            applyRemoteVideoQuality();
            updateE2eeStatus(room);
        })
            .on(RoomEvent.ParticipantDisconnected, (p) => {
            fileReceiverRef.current.senderLeft(p.identity);
            // A departed participant's raised hand must not stay badged.
            setRaisedHands((prev) => prev.filter((id) => id !== p.identity));
            syncParticipants();
            applyRemoteVideoQuality();
            updateE2eeStatus(room);
        })
            .on(RoomEvent.ParticipantEncryptionStatusChanged, () => updateE2eeStatus(room))
            .on(RoomEvent.EncryptionError, (err) => {
            console.warn("[lkCall] encryption error:", err);
            updateE2eeStatus(room);
        })
            .on(RoomEvent.TrackSubscribed, () => {
            syncParticipants();
            applyRemoteVideoQuality();
        })
            // A screen publication can outlive its ended MediaStreamTrack in the
            // remote participant map. Rebuild immediately on publish/unpublish so
            // the camera publication becomes the tile source without requiring a
            // camera toggle.
            .on(RoomEvent.TrackPublished, syncParticipants)
            .on(RoomEvent.TrackUnpublished, syncParticipants)
            .on(RoomEvent.TrackUnsubscribed, syncParticipants)
            .on(RoomEvent.TrackMuted, syncParticipants)
            .on(RoomEvent.TrackUnmuted, syncParticipants)
            // Speaker changes fire often — only flip the `speaking` flags instead
            // of rebuilding streams (which would churn every tile's media sink).
            .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
            const ids = new Set(speakers.map((s) => s.identity));
            activeSpeakersRef.current = ids;
            setParticipants((prev) => {
                let changed = false;
                const next = prev.map((p) => {
                    const s = ids.has(p.deviceId);
                    if (!!p.speaking === s)
                        return p;
                    changed = true;
                    return { ...p, speaking: s };
                });
                return changed ? next : prev;
            });
        })
            .on(RoomEvent.LocalTrackPublished, () => {
            syncLocalStream();
            syncParticipants();
        })
            .on(RoomEvent.LocalTrackUnpublished, (publication) => {
            syncLocalStream();
            syncParticipants();
            if (publication?.source !== Track.Source.ScreenShare)
                return;
            const scope = {
                room,
                callId: id,
                generation: roomScreenShareGeneration,
            };
            if (roomRef.current !== scope.room ||
                callIdRef.current !== scope.callId ||
                screenShareGenerationRef.current !== scope.generation)
                return;
            if (screenShareStopInProgressRef.current)
                return;
            if (screenShareTransitionRef.current) {
                screenShareEndedDuringTransitionRef.current = true;
                return;
            }
            void stopLocalScreenShareRef.current?.(scope);
        })
            .on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
            // Remote quality is useful for rendering badges, but relay rescue
            // must only act on this device's own connection.
            if (participant.identity !== room.localParticipant.identity)
                return;
            if (quality === ConnectionQuality.Poor) {
                latchMobileThermalCeiling(room);
                scheduleRelayRecovery(room, "poor-quality", POOR_QUALITY_GRACE_MS);
            }
            else if (quality === ConnectionQuality.Lost) {
                latchMobileThermalCeiling(room);
                setConnectionRecovery("recovering");
                scheduleRelayRecovery(room, "connection-lost", 0);
            }
            else if (quality === ConnectionQuality.Good ||
                quality === ConnectionQuality.Excellent) {
                clearPendingRecovery();
                if (!recoveryInFlightRef.current)
                    setConnectionRecovery(null);
            }
        })
            .on(RoomEvent.Reconnecting, () => {
            if (roomRef.current === room)
                setConnectionPhase("encrypting");
            if (roomRef.current === room &&
                !relayModeRef.current &&
                !recoveryInFlightRef.current &&
                !wasRecentlyBackgrounded()) {
                setConnectionRecovery("recovering");
                scheduleRelayRecovery(room, "media-reconnecting", MEDIA_RECONNECT_GRACE_MS);
            }
        })
            .on(RoomEvent.Reconnected, () => {
            if (roomRef.current !== room || recoveryInFlightRef.current)
                return;
            clearPendingRecovery();
            applyRemoteVideoQuality();
            updateE2eeStatus(room);
            setConnectionRecovery(null);
        })
            .on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
            if (topic === FILE_SHARE_TOPIC && participant) {
                fileReceiverRef.current.handleData(payload, participant.identity);
            }
        })
            .on(RoomEvent.Disconnected, () => {
            if (roomRef.current === room)
                setConnectionPhase("encrypting");
            // server-side drop / network loss while we still thought we were in
            if (roomRef.current === room && callStateRef.current !== "idle") {
                // A direct route that died gets one automatic relay-only rescue.
                // Background suspension is intentionally excluded: iOS cannot
                // reliably recapture media there, so retain the existing rejoin UI.
                if (!relayModeRef.current &&
                    !recoveryInFlightRef.current &&
                    !wasRecentlyBackgrounded()) {
                    setConnectionRecovery("recovering");
                    scheduleRelayRecovery(room, "disconnected", 0);
                    return;
                }
                const info = callIdRef.current
                    ? {
                        callId: callIdRef.current,
                        video: isVideoCallRef.current,
                        ts: Date.now(),
                        forceRelay: relayModeRef.current,
                        muted: isMutedRef.current,
                        mutedByHost: mutedByHostRef.current,
                        cameraOff: isCameraOffRef.current,
                    }
                    : null;
                rejoinInfoRef.current = info;
                setCallDroppedByBackground(wasRecentlyBackgrounded());
                setCallDropped(true);
                teardown({ keepRejoin: !!info });
            }
        });
        if (seq !== connectSeqRef.current || options.isCancelled?.()) {
            room.removeAllListeners();
            if (e2ee)
                e2ee.worker.terminate();
            return false;
        }
        try {
            // Wrap room.connect with a timeout so a blocked WebSocket or ICE
            // handshake surfaces as a distinct "connect timed out" category rather
            // than hanging indefinitely or producing an opaque network error.
            //
            // IMPORTANT: when the timer fires before connect() resolves, we must
            // disconnect the room if connect() eventually succeeds — otherwise the
            // participant would remain in the room as an orphan (consuming media
            // resources) while the UI already reported a failed call.
            await new Promise((resolve, reject) => {
                let settled = false;
                // orphan=true means the timeout fired before connect() resolved;
                // the delayed resolve handler will disconnect the room.
                let orphan = false;
                const timer = setTimeout(() => {
                    if (settled)
                        return;
                    settled = true;
                    orphan = true;
                    reject(categorizedError('room connect timed out', CONNECT_TIMEOUT_ERROR));
                }, CONNECT_TIMEOUT_MS);
                // rtcConfig is a supported Room.connect option in livekit-client.
                // Do not force every healthy caller through TURN: only a client
                // whose direct UDP path proved unusable receives relay-only ICE.
                room.connect(access.url, access.token, options.forceRelay
                    ? { rtcConfig: { iceTransportPolicy: "relay" } }
                    : undefined).then(() => {
                    clearTimeout(timer);
                    if (orphan) {
                        // Timeout already fired — disconnect this now-orphaned room
                        // so we don't leave a ghost participant in the LiveKit room.
                        room.removeAllListeners();
                        room.disconnect().catch(() => { });
                        return;
                    }
                    if (!settled) {
                        settled = true;
                        resolve();
                    }
                }, (err) => {
                    clearTimeout(timer);
                    if (settled)
                        return; // timeout already rejected
                    settled = true;
                    // Tag unclassified connect errors so the UI can show a "firewall?"
                    // hint instead of the generic SFU-unreachable fallback.
                    if (!getSfuCategory(err)) {
                        const msg = err instanceof Error ? err.message : String(err);
                        reject(categorizedError(msg, CONNECT_FAILED_ERROR));
                    }
                    else {
                        reject(err);
                    }
                });
            });
        }
        catch (err) {
            room.removeAllListeners();
            if (e2ee)
                e2ee.worker.terminate();
            throw err;
        }
        if (seq !== connectSeqRef.current || options.isCancelled?.()) {
            room.removeAllListeners();
            room.disconnect().catch(() => { });
            if (e2ee)
                e2ee.worker.terminate();
            return false;
        }
        roomRef.current = room;
        relayModeRef.current = !!options.forceRelay;
        e2eeWorkerRef.current = e2ee ? e2ee.worker : null;
        e2eeKeyProviderRef.current = e2ee ? e2ee.keyProvider : null;
        setCallIdBoth(id);
        setVideoBoth(video);
        setCallDropped(false);
        setCallDroppedByBackground(false);
        // Room transport is connected. Only frame-E2EE (or mesh p2p in useCall)
        // may advance this UI to secure; unsupported/partial E2EE stays amber.
        updateE2eeStatus(room);
        const isSuperseded = () => seq !== connectSeqRef.current || !!options.isCancelled?.();
        const abandonConnectedRoom = () => {
            room.removeAllListeners();
            room.disconnect().catch(() => { });
            if (roomRef.current === room) {
                roomRef.current = null;
                relayModeRef.current = false;
            }
            if (e2ee && e2eeWorkerRef.current === e2ee.worker) {
                e2ee.worker.terminate();
                e2eeWorkerRef.current = null;
                e2eeKeyProviderRef.current = null;
            }
            return false;
        };
        if (isSuperseded())
            return abandonConnectedRoom();
        // Microphone is mandatory: joining without any local media leaves a
        // silent black tile for everyone else (guests auto-join, so the browser
        // permission prompt is their only gate). If the mic can't be enabled,
        // leave the room and surface a "media denied" error instead of joining.
        const desiredMuted = options.restore?.muted ?? false;
        const desiredMutedByHost = options.restore?.mutedByHost ?? false;
        isMutedRef.current = desiredMuted;
        mutedByHostRef.current = desiredMutedByHost;
        setIsMuted(desiredMuted);
        try {
            await room.localParticipant.setMicrophoneEnabled(!desiredMuted);
        }
        catch (err) {
            if (isSuperseded())
                return abandonConnectedRoom();
            console.warn("[lkCall] mic enable failed — leaving room:", err);
            room.removeAllListeners();
            room.disconnect().catch(() => { });
            roomRef.current = null;
            if (e2ee)
                e2ee.worker.terminate();
            e2eeWorkerRef.current = null;
            e2eeKeyProviderRef.current = null;
            throw new Error(MEDIA_DENIED_ERROR);
        }
        if (isSuperseded())
            return abandonConnectedRoom();
        const desiredCameraOff = video ? (options.restore?.cameraOff ?? false) : false;
        isCameraOffRef.current = desiredCameraOff;
        setIsCameraOff(desiredCameraOff);
        if (video && !desiredCameraOff) {
            // Camera failure is tolerable — stay in the call audio-only.
            try {
                await room.localParticipant.setCameraEnabled(true, {
                    facingMode: facingRef.current,
                    resolution: isMobileDevice()
                        ? mobileThermalCeilingRef.current
                            ? MOBILE_THERMAL_CAPTURE
                            : { width: 640, height: 360, frameRate: 24 }
                        : { width: 1280, height: 720, frameRate: 30 },
                });
            }
            catch (err) {
                if (isSuperseded())
                    return abandonConnectedRoom();
                console.warn("[lkCall] camera enable failed — staying audio-only:", err);
                isCameraOffRef.current = true;
                setIsCameraOff(true);
            }
            if (isSuperseded())
                return abandonConnectedRoom();
        }
        syncLocalStream();
        syncParticipants();
        activeRoomNameRef.current = roomName;
        setStateBoth(room.remoteParticipants.size > 0 ? "in-call" : "calling");
        // presence beacon → idle devices learn about the ongoing call
        if (beaconTimerRef.current)
            clearInterval(beaconTimerRef.current);
        beaconTimerRef.current = setInterval(() => {
            if (roomRef.current === room && callIdRef.current) {
                sendRef.current({
                    subtype: "lk-beacon",
                    callId: callIdRef.current,
                    payload: { video: isVideoCallRef.current },
                });
            }
        }, BEACON_INTERVAL_MS);
        return true;
    }, [
        roomName,
        deviceId,
        guestToken,
        deviceSessionToken,
        signWalletMessage,
        syncParticipants,
        syncLocalStream,
        applyRemoteVideoQuality,
        latchMobileThermalCeiling,
        teardown,
        wasRecentlyBackgrounded,
        scheduleRelayRecovery,
        clearPendingRecovery,
    ]);
    const failWithError = useCallback((err, opts) => {
        console.error("[lkCall] connect failed:", err);
        // Store the typed category sentinel when present so the UI can show a
        // specific, actionable message (token rejected / network / timeout).
        // Raw messages are logged above; the state holds only the sentinel or
        // the MEDIA_DENIED_ERROR sentinel — never a raw stack trace.
        const category = getSfuCategory(err);
        const error = category ?? (err instanceof Error ? err.message : String(err));
        const retryCallId = callIdRef.current;
        const retryableMediaDenial = [MEDIA_DENIED_ERROR, E2EE_KEY_UNAVAILABLE_ERROR, E2EE_SETUP_FAILED_ERROR].includes(error) && !!retryCallId;
        if (retryableMediaDenial) {
            rejoinInfoRef.current = {
                callId: retryCallId,
                video: isVideoCallRef.current,
                ts: Date.now(),
                forceRelay: relayModeRef.current,
                announceOnConnect: !!opts?.announceOnConnect,
                muted: isMutedRef.current,
                mutedByHost: mutedByHostRef.current,
                cameraOff: isCameraOffRef.current,
            };
        }
        teardown({ keepRejoin: retryableMediaDenial });
        // A failed room disconnect can also emit the generic dropped state. The
        // actionable media error owns the surface; never stack both mobile bars.
        setCallDropped(false);
        setCallDroppedByBackground(false);
        setCallError(error);
    }, [teardown]);
    /**
     * Replace an unusable direct-UDP room with a relay-only room. Identity,
     * room/call id and E2EE source material stay unchanged; only this client
     * reconnects, so healthy participants are not interrupted.
     */
    const recoverWithRelay = useCallback(async (failedRoom, reason) => {
        if (roomRef.current !== failedRoom ||
            callStateRef.current === "idle" ||
            relayModeRef.current ||
            recoveryInFlightRef.current ||
            wasRecentlyBackgrounded()) {
            return;
        }
        const id = callIdRef.current;
        if (!id)
            return;
        const video = isVideoCallRef.current;
        const generation = ++recoveryGenerationRef.current;
        const isCancelled = () => generation !== recoveryGenerationRef.current ||
            callStateRef.current === "idle";
        const currentIntent = () => ({
            muted: isMutedRef.current,
            mutedByHost: mutedByHostRef.current,
            cameraOff: isCameraOffRef.current,
        });
        let intent = currentIntent();
        const parkForRejoin = (background) => {
            intent = currentIntent();
            rejoinInfoRef.current = {
                callId: id,
                video,
                ts: Date.now(),
                forceRelay: true,
                ...intent,
            };
            teardown({ keepRejoin: true });
            setCallDropped(true);
            setCallDroppedByBackground(background);
        };
        recoveryInFlightRef.current = true;
        clearPendingRecovery();
        setConnectionRecovery("recovering");
        console.warn(`[lkCall] ${reason}; rebuilding connection through TURN relay`);
        try {
            // Free bandwidth immediately while the broken room is still alive.
            // This does not change the user's camera preference; it is restored on
            // the relay room after the transport is healthy.
            if (video && !intent.cameraOff) {
                await failedRoom.localParticipant.setCameraEnabled(false).catch(() => { });
            }
            if (isCancelled())
                return;
            if (wasRecentlyBackgrounded()) {
                parkForRejoin(true);
                return;
            }
            if (!explicitMediaPermissionRef.current ||
                !(await browserMicrophonePermissionIsReusable())) {
                if (isCancelled())
                    return;
                // Permission is no longer granted. Do not let an automatic recovery
                // trigger a browser sheet; park the call for an explicit Rejoin tap.
                parkForRejoin(false);
                return;
            }
            if (!(await disposeRoomForRecovery(failedRoom)))
                return;
            if (isCancelled())
                return;
            if (wasRecentlyBackgrounded()) {
                parkForRejoin(true);
                return;
            }
            // Capture intent after disposal so a mute/camera action that raced the
            // first await is not overwritten by an older snapshot.
            intent = currentIntent();
            const connected = await connect(video, id, {
                forceRelay: true,
                restore: intent,
                isCancelled: () => isCancelled() || wasRecentlyBackgrounded(),
            });
            if (isCancelled())
                return;
            if (!connected) {
                if (wasRecentlyBackgrounded())
                    parkForRejoin(true);
                setConnectionRecovery(null);
                return;
            }
            if (wasRecentlyBackgrounded()) {
                parkForRejoin(true);
                return;
            }
            setConnectionRecovery("relay");
            if (recoveryNoticeTimerRef.current)
                clearTimeout(recoveryNoticeTimerRef.current);
            recoveryNoticeTimerRef.current = setTimeout(() => {
                recoveryNoticeTimerRef.current = null;
                setConnectionRecovery(null);
            }, RELAY_NOTICE_MS);
            console.info("[lkCall] automatic TURN relay recovery succeeded");
        }
        catch (err) {
            if (isCancelled())
                return;
            if (wasRecentlyBackgrounded()) {
                parkForRejoin(true);
                return;
            }
            console.error("[lkCall] automatic TURN relay recovery failed:", err);
            setCallError(RELAY_RECOVERY_FAILED_ERROR);
            rejoinInfoRef.current = {
                callId: id,
                video,
                ts: Date.now(),
                forceRelay: true,
                ...intent,
            };
            teardown({ keepRejoin: true });
        }
        finally {
            recoveryInFlightRef.current = false;
        }
    }, [
        clearPendingRecovery,
        connect,
        disposeRoomForRecovery,
        teardown,
        wasRecentlyBackgrounded,
    ]);
    recoveryHandlerRef.current = recoverWithRelay;
    const startCall = useCallback(async (opts) => {
        if (!enabled || callStateRef.current !== "idle")
            return;
        rejoinInfoRef.current = null;
        setCanRejoin(false);
        clearStoredRejoin();
        const video = !!opts?.video;
        const id = genCallId();
        setCallIdBoth(id);
        setVideoBoth(video);
        setStateBoth("calling");
        let attemptSeq = connectSeqRef.current;
        try {
            await preflightBrowserMediaPermissions(video, facingRef.current);
            if (attemptSeq !== connectSeqRef.current || currentCallState() !== "calling")
                return;
            explicitMediaPermissionRef.current = true;
            const connectPromise = connect(video, id);
            attemptSeq = connectSeqRef.current;
            const connected = await connectPromise;
            if (attemptSeq !== connectSeqRef.current || currentCallState() === "idle")
                return;
            // only ring others if we actually made it into the room (not canceled)
            if (connected)
                sendRef.current({ subtype: "lk-invite", callId: id, payload: { video } });
        }
        catch (err) {
            if (attemptSeq !== connectSeqRef.current || currentCallState() === "idle")
                return;
            failWithError(err, { announceOnConnect: true });
        }
    }, [enabled, connect, failWithError]);
    const acceptCall = useCallback(async () => {
        if (callStateRef.current !== "incoming" || !callIdRef.current)
            return;
        rejoinInfoRef.current = null;
        setCanRejoin(false);
        clearStoredRejoin();
        const id = callIdRef.current;
        const video = incomingVideoRef.current;
        setVideoBoth(video);
        setIncomingFrom(null);
        setStateBoth("calling");
        let attemptSeq = connectSeqRef.current;
        try {
            await preflightBrowserMediaPermissions(video, facingRef.current);
            if (attemptSeq !== connectSeqRef.current || currentCallState() !== "calling")
                return;
            explicitMediaPermissionRef.current = true;
            const connectPromise = connect(video, id);
            attemptSeq = connectSeqRef.current;
            await connectPromise;
        }
        catch (err) {
            if (attemptSeq !== connectSeqRef.current || currentCallState() === "idle")
                return;
            failWithError(err);
        }
    }, [connect, failWithError]);
    const rejectCall = useCallback(() => {
        if (callStateRef.current !== "incoming")
            return;
        if (callIdRef.current && incomingFrom) {
            sendRef.current({ subtype: "lk-reject", callId: callIdRef.current, to: incomingFrom });
        }
        teardown();
    }, [incomingFrom, teardown]);
    const hangup = useCallback(() => {
        const othersRemain = (roomRef.current?.remoteParticipants.size ?? 0) > 0;
        if (othersRemain && callIdRef.current) {
            rejoinInfoRef.current = {
                callId: callIdRef.current,
                video: isVideoCallRef.current,
                ts: Date.now(),
                forceRelay: relayModeRef.current,
                muted: isMutedRef.current,
                mutedByHost: mutedByHostRef.current,
                cameraOff: isCameraOffRef.current,
            };
        }
        teardown({ keepRejoin: othersRemain && !!callIdRef.current });
    }, [teardown]);
    const leaveCall = useCallback(() => {
        // Unlike a temporary hangup, an explicit guest Leave must not persist a
        // rejoin descriptor. teardown also releases local tracks/fullscreen-share
        // state without affecting the invitation/chat session.
        rejoinInfoRef.current = null;
        clearStoredRejoin();
        setCallDropped(false);
        setCallDroppedByBackground(false);
        setOngoingCall(null);
        setCallError(null);
        teardown({ keepRejoin: false });
    }, [teardown]);
    const toggleMute = useCallback(() => {
        const next = !isMutedRef.current;
        isMutedRef.current = next;
        // Any manual toggle makes the mute state the user's OWN choice — the
        // host's unmute request must no longer touch it.
        mutedByHostRef.current = false;
        setIsMuted(next);
        roomRef.current?.localParticipant.setMicrophoneEnabled(!next).catch(() => { });
        // Unmuting yourself lowers your raised hand — the "request" is fulfilled.
        if (!next && handRaisedRef.current && callIdRef.current) {
            handRaisedRef.current = false;
            setHandRaised(false);
            sendRef.current({ subtype: "lk-hand", callId: callIdRef.current, payload: { raised: false } });
        }
    }, []);
    /** Ask every other participant to mute their microphone (host control).
     *  Sent over the chat WS (the relay drops our own copy); each client mutes
     *  ITSELF on receipt — this is a courtesy broadcast, not enforcement. */
    const muteAll = useCallback(() => {
        if (callStateRef.current !== "in-call" || !callIdRef.current)
            return;
        setMuteAllActive(true);
        sendRef.current({ subtype: "lk-mute-request", callId: callIdRef.current });
    }, []);
    /** Host control: undo a previous mute request. Clients only comply when
     *  their mute originated from the host's request. */
    const unmuteAll = useCallback(() => {
        if (callStateRef.current !== "in-call" || !callIdRef.current) {
            console.warn(`[livekit] unmuteAll ignored (state=${callStateRef.current}, callId=${callIdRef.current})`);
            return;
        }
        console.debug(`[livekit] sending lk-unmute-request (callId=${callIdRef.current})`);
        setMuteAllActive(false);
        sendRef.current({ subtype: "lk-unmute-request", callId: callIdRef.current });
    }, []);
    /** Host control: targeted mute/unmute of ONE participant. Uses the same
     *  request subtypes with a `to` address — only the target complies. */
    const setParticipantMuted = useCallback((targetDeviceId, muted) => {
        if (callStateRef.current !== "in-call" || !callIdRef.current)
            return;
        // Distinct subtypes: old clients treat every *-request as broadcast (they
        // have no `to` guard), so reusing them would mute the whole room. Old
        // clients simply ignore the unknown *-one subtypes instead.
        sendRef.current({
            subtype: muted ? "lk-mute-one" : "lk-unmute-one",
            callId: callIdRef.current,
            to: targetDeviceId,
        });
        // If this unmute leaves nobody else muted, the room is effectively back
        // to "unmuted" — flip the global mute-all toggle off so its state matches
        // reality (as if unmute-all had been pressed).
        if (!muted) {
            let othersStillMuted = false;
            roomRef.current?.remoteParticipants.forEach((p) => {
                if (p.identity === targetDeviceId)
                    return;
                const micPub = p.getTrackPublication(Track.Source.Microphone);
                if (!micPub || micPub.isMuted)
                    othersStillMuted = true;
            });
            if (!othersStillMuted)
                setMuteAllActive(false);
        }
    }, []);
    /** Raise/lower own hand — a courtesy broadcast every participant renders. */
    const toggleHand = useCallback(() => {
        if (callStateRef.current !== "in-call" || !callIdRef.current)
            return;
        const next = !handRaisedRef.current;
        handRaisedRef.current = next;
        setHandRaised(next);
        sendRef.current({ subtype: "lk-hand", callId: callIdRef.current, payload: { raised: next } });
    }, []);
    // The hand signal is transient (no relay replay): whenever the set of
    // participants changes (someone joined/rejoined), re-announce our raised
    // hand so late joiners see it too.
    const participantIdsKey = participants.map((p) => p.deviceId).sort().join(",");
    useEffect(() => {
        if (handRaisedRef.current && callIdRef.current && callStateRef.current === "in-call") {
            sendRef.current({ subtype: "lk-hand", callId: callIdRef.current, payload: { raised: true } });
        }
    }, [participantIdsKey]);
    /** Host control: remove a participant. The target leaves the room on
     *  receipt; the relay drops this subtype from guest sockets. */
    const kickParticipant = useCallback((targetDeviceId) => {
        if (callStateRef.current !== "in-call" || !callIdRef.current)
            return;
        sendRef.current({ subtype: "lk-kick", callId: callIdRef.current, to: targetDeviceId });
    }, []);
    const toggleCamera = useCallback(() => {
        const next = !isCameraOffRef.current;
        isCameraOffRef.current = next;
        setIsCameraOff(next);
        const participant = roomRef.current?.localParticipant;
        const enableOptions = !next && isMobileDevice() && mobileThermalCeilingRef.current
            ? { facingMode: facingRef.current, resolution: MOBILE_THERMAL_CAPTURE }
            : undefined;
        if (!participant)
            return;
        (enableOptions
            ? participant.setCameraEnabled(true, enableOptions)
            : participant.setCameraEnabled(!next))
            .then(() => syncLocalStream())
            .catch(() => { });
    }, [syncLocalStream]);
    const switchCamera = useCallback(async () => {
        const room = roomRef.current;
        if (!room || !isVideoCallRef.current)
            return;
        const track = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
        if (!track)
            return;
        const next = desiredFacingRef.current === "user" ? "environment" : "user";
        desiredFacingRef.current = next;
        try {
            await restartLocalCamera(room, next);
            syncLocalStream();
        }
        catch (err) {
            console.warn("[lkCall] switchCamera failed:", err);
        }
    }, [restartLocalCamera, syncLocalStream]);
    const microphoneSwitchQueue = useRef(Promise.resolve());
    const microphoneSwitchGeneration = useRef(0);
    const switchMicrophone = useCallback(async (micId) => {
        const room = roomRef.current;
        if (!room)
            return;
        const generation = ++microphoneSwitchGeneration.current;
        const operation = microphoneSwitchQueue.current.then(async () => {
            if (roomRef.current !== room || generation !== microphoneSwitchGeneration.current)
                return;
            try {
                let target = micId;
                if (!target) {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const mics = devices.filter((d) => d.kind === "audioinput" && d.deviceId);
                    if (mics.length < 2)
                        return;
                    const current = room.getActiveDevice("audioinput");
                    const idx = mics.findIndex((m) => m.deviceId === current);
                    target = mics[(idx + 1) % mics.length].deviceId;
                }
                if (roomRef.current !== room || generation !== microphoneSwitchGeneration.current)
                    return;
                await room.switchActiveDevice("audioinput", target);
                if (roomRef.current !== room || generation !== microphoneSwitchGeneration.current)
                    return;
                syncLocalStream();
            }
            catch (err) {
                if (roomRef.current !== room || generation !== microphoneSwitchGeneration.current)
                    return;
                console.warn("[lkCall] switchMicrophone failed:", err);
                window.dispatchEvent(new Event("call-microphone-route-error"));
            }
        });
        microphoneSwitchQueue.current = operation;
        await operation;
    }, [syncLocalStream]);
    const releaseScreenShareOwnership = useCallback((scope) => {
        if (scope &&
            (roomRef.current !== scope.room ||
                callIdRef.current !== scope.callId ||
                screenShareGenerationRef.current !== scope.generation))
            return;
        const activeCallId = scope?.callId ?? callIdRef.current;
        if (activeCallId && screenShareOwnerRef.current === deviceIdRef.current) {
            sendRef.current({ subtype: "lk-screen-share-release", callId: activeCallId });
            screenShareOwnerByCallRef.current.set(activeCallId, null);
            screenShareOwnerRef.current = null;
            setScreenShareOwnerId(null);
            syncParticipantsRef.current();
        }
    }, []);
    const restoreCameraAfterScreenShare = useCallback(async (scope) => {
        const { room } = scope;
        const shouldRestore = () => roomRef.current === room &&
            callIdRef.current === scope.callId &&
            screenShareGenerationRef.current === scope.generation &&
            isVideoCallRef.current &&
            currentCallState() !== "idle" &&
            !isCameraOffRef.current;
        if (!shouldRestore())
            return;
        const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
        const cameraTrack = publication?.track;
        const mediaTrack = cameraTrack?.mediaStreamTrack;
        if (cameraTrack && !publication?.isMuted && mediaTrack?.readyState !== "ended")
            return;
        try {
            await room.localParticipant.setCameraEnabled(true, {
                facingMode: facingRef.current,
                resolution: isMobileDevice() && mobileThermalCeilingRef.current
                    ? MOBILE_THERMAL_CAPTURE
                    : isMobileDevice()
                        ? { width: 640, height: 360, frameRate: 24 }
                        : { width: 1280, height: 720, frameRate: 30 },
            });
            if (!shouldRestore()) {
                await room.localParticipant.setCameraEnabled(false).catch(() => { });
            }
        }
        catch (err) {
            if (shouldRestore()) {
                console.warn("[lkCall] camera restore after screen share failed:", err);
                isCameraOffRef.current = true;
                setIsCameraOff(true);
            }
        }
    }, []);
    const stopLocalScreenShare = useCallback(async (requestedScope) => {
        const room = requestedScope?.room ?? roomRef.current;
        const callId = requestedScope?.callId ?? callIdRef.current;
        const scope = room && callId
            ? {
                room,
                callId,
                generation: requestedScope?.generation ?? screenShareGenerationRef.current,
            }
            : null;
        const scopeIsCurrent = () => !!scope &&
            roomRef.current === scope.room &&
            callIdRef.current === scope.callId &&
            screenShareGenerationRef.current === scope.generation;
        if (!scope) {
            isScreenSharingRef.current = false;
            setIsScreenSharing(false);
            releaseScreenShareOwnership();
            return;
        }
        if (!scopeIsCurrent())
            return;
        if (screenShareTransitionRef.current) {
            if (!screenShareStopInProgressRef.current) {
                screenShareEndedDuringTransitionRef.current = true;
            }
            return;
        }
        screenShareStopInProgressRef.current = true;
        screenShareTransitionRef.current = true;
        screenShareEndedDuringTransitionRef.current = false;
        const operation = ++screenShareTransitionOperationRef.current;
        const scopedRoom = scope.room;
        const activeShare = scopedRoom.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track;
        try {
            // Disable pixels immediately while retaining the LocalTrack identity
            // through unpublish. Stopping first can leave mobile receivers with an
            // ended screen sender until a later camera toggle; the stale-track guard
            // in syncParticipants then falls back to the live camera publication.
            if (activeShare?.mediaStreamTrack) {
                activeShare.mediaStreamTrack.enabled = false;
            }
            await scopedRoom.localParticipant.setScreenShareEnabled(false);
            stopLiveKitTrackImmediately(activeShare);
            if (!scopeIsCurrent() || screenShareTransitionOperationRef.current !== operation)
                return;
            isScreenSharingRef.current = false;
            setIsScreenSharing(false);
            await restoreCameraAfterScreenShare(scope);
            if (!scopeIsCurrent() || screenShareTransitionOperationRef.current !== operation)
                return;
            syncLocalStream();
        }
        catch (err) {
            stopLiveKitTrackImmediately(activeShare);
            if (!scopeIsCurrent() || screenShareTransitionOperationRef.current !== operation)
                return;
            console.warn("[lkCall] stopping screen share failed:", err);
            isScreenSharingRef.current = false;
            setIsScreenSharing(false);
            await restoreCameraAfterScreenShare(scope);
            if (!scopeIsCurrent() || screenShareTransitionOperationRef.current !== operation)
                return;
            syncLocalStream();
        }
        finally {
            if (screenShareTransitionOperationRef.current !== operation)
                return;
            screenShareEndedDuringTransitionRef.current = false;
            screenShareTransitionRef.current = false;
            screenShareStopInProgressRef.current = false;
            if (scopeIsCurrent())
                releaseScreenShareOwnership(scope);
        }
    }, [releaseScreenShareOwnership, restoreCameraAfterScreenShare, syncLocalStream]);
    stopLocalScreenShareRef.current = stopLocalScreenShare;
    const startScreenShareAfterClaim = useCallback(async () => {
        const room = roomRef.current;
        if (!room ||
            !isVideoCallRef.current ||
            !callIdRef.current ||
            screenShareOwnerRef.current !== deviceIdRef.current ||
            screenShareTransitionRef.current)
            return;
        const scope = {
            room,
            callId: callIdRef.current,
            generation: screenShareGenerationRef.current,
        };
        const scopeIsCurrent = () => roomRef.current === scope.room &&
            callIdRef.current === scope.callId &&
            screenShareGenerationRef.current === scope.generation;
        screenShareTransitionRef.current = true;
        screenShareEndedDuringTransitionRef.current = false;
        const operation = ++screenShareTransitionOperationRef.current;
        // Do not leave the camera preview in a tab/window/monitor frame captured
        // while LiveKit is waiting for the browser's display-source picker.
        setLocalStream(null);
        try {
            await room.localParticipant.setScreenShareEnabled(true, {
                selfBrowserSurface: "exclude",
            });
            if (!scopeIsCurrent() ||
                screenShareTransitionOperationRef.current !== operation ||
                screenShareOwnerRef.current !== deviceIdRef.current) {
                const staleShare = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track;
                stopLiveKitTrackImmediately(staleShare);
                await room.localParticipant.setScreenShareEnabled(false).catch(() => { });
                if (scopeIsCurrent() && screenShareTransitionOperationRef.current === operation) {
                    isScreenSharingRef.current = false;
                    setIsScreenSharing(false);
                    syncLocalStream();
                }
                return;
            }
            const actuallySharing = !!room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track;
            isScreenSharingRef.current = actuallySharing;
            setIsScreenSharing(actuallySharing);
            const screenMediaTrack = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track?.mediaStreamTrack;
            if (screenMediaTrack && actuallySharing) {
                screenMediaTrack.onended = () => {
                    if (!scopeIsCurrent())
                        return;
                    if (screenShareStopInProgressRef.current)
                        return;
                    if (screenShareTransitionRef.current) {
                        screenShareEndedDuringTransitionRef.current = true;
                        return;
                    }
                    void stopLocalScreenShareRef.current?.(scope);
                };
            }
            syncLocalStream();
            if (!actuallySharing)
                releaseScreenShareOwnership(scope);
        }
        catch (err) {
            const staleShare = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track;
            stopLiveKitTrackImmediately(staleShare);
            await room.localParticipant.setScreenShareEnabled(false).catch(() => { });
            if (!scopeIsCurrent() || screenShareTransitionOperationRef.current !== operation)
                return;
            console.warn("[lkCall] screen share failed:", err);
            syncLocalStream();
            releaseScreenShareOwnership(scope);
        }
        finally {
            if (screenShareTransitionOperationRef.current !== operation)
                return;
            screenShareTransitionRef.current = false;
            if (screenShareEndedDuringTransitionRef.current) {
                screenShareEndedDuringTransitionRef.current = false;
                void stopLocalScreenShareRef.current?.(scope);
            }
        }
    }, [releaseScreenShareOwnership, syncLocalStream]);
    startScreenShareAfterClaimRef.current = startScreenShareAfterClaim;
    const toggleScreenShare = useCallback(async () => {
        const room = roomRef.current;
        if (!room ||
            !isVideoCallRef.current ||
            !callIdRef.current ||
            screenShareTransitionRef.current ||
            screenShareClaimPendingRef.current)
            return;
        if (isScreenSharingRef.current) {
            await stopLocalScreenShare();
            return;
        }
        if (screenShareOwnerRef.current &&
            screenShareOwnerRef.current !== deviceIdRef.current)
            return;
        if (screenShareOwnerRef.current === deviceIdRef.current) {
            await startScreenShareAfterClaim();
            return;
        }
        screenShareClaimPendingRef.current = true;
        setScreenShareClaimPending(true);
        sendRef.current({ subtype: "lk-screen-share-claim", callId: callIdRef.current });
        if (screenShareClaimTimerRef.current)
            clearTimeout(screenShareClaimTimerRef.current);
        screenShareClaimTimerRef.current = setTimeout(() => {
            screenShareClaimTimerRef.current = null;
            screenShareClaimPendingRef.current = false;
            setScreenShareClaimPending(false);
        }, 5000);
    }, [startScreenShareAfterClaim, stopLocalScreenShare]);
    // Screen-share ownership belongs to the chat WebSocket session. If that
    // transport drops, stop publishing immediately; the relay releases the lock
    // when it observes the owning socket close.
    useEffect(() => {
        if (isConnected)
            return;
        if (screenShareClaimTimerRef.current) {
            clearTimeout(screenShareClaimTimerRef.current);
            screenShareClaimTimerRef.current = null;
        }
        screenShareClaimPendingRef.current = false;
        setScreenShareClaimPending(false);
        if (callIdRef.current)
            screenShareOwnerByCallRef.current.set(callIdRef.current, null);
        screenShareOwnerRef.current = null;
        setScreenShareOwnerId(null);
        syncParticipants();
        const room = roomRef.current;
        const callId = callIdRef.current;
        if (!room || !callId || (!isScreenSharingRef.current && !screenShareTransitionRef.current))
            return;
        void stopLocalScreenShare({
            room,
            callId,
            generation: screenShareGenerationRef.current,
        });
    }, [isConnected, stopLocalScreenShare, syncParticipants]);
    const rejoinCall = useCallback(async () => {
        const info = rejoinInfoRef.current;
        if (!info || callStateRef.current !== "idle")
            return;
        rejoinInfoRef.current = null;
        setCanRejoin(false);
        clearStoredRejoin();
        setCallIdBoth(info.callId);
        setVideoBoth(info.video);
        setStateBoth("calling");
        if (info.forceRelay)
            setConnectionRecovery("recovering");
        let attemptSeq = connectSeqRef.current;
        try {
            await preflightBrowserMediaPermissions(info.video, facingRef.current);
            if (attemptSeq !== connectSeqRef.current || currentCallState() !== "calling")
                return;
            explicitMediaPermissionRef.current = true;
            const connectPromise = connect(info.video, info.callId, {
                forceRelay: info.forceRelay,
                restore: {
                    muted: info.muted,
                    mutedByHost: info.mutedByHost,
                    cameraOff: info.cameraOff,
                },
            });
            attemptSeq = connectSeqRef.current;
            const connected = await connectPromise;
            if (attemptSeq !== connectSeqRef.current || currentCallState() === "idle")
                return;
            if (connected && info.announceOnConnect) {
                sendRef.current({
                    subtype: "lk-invite",
                    callId: info.callId,
                    payload: { video: info.video },
                });
            }
            if (info.forceRelay) {
                setConnectionRecovery("relay");
                recoveryNoticeTimerRef.current = setTimeout(() => {
                    recoveryNoticeTimerRef.current = null;
                    setConnectionRecovery(null);
                }, RELAY_NOTICE_MS);
            }
        }
        catch (err) {
            if (attemptSeq !== connectSeqRef.current || currentCallState() === "idle")
                return;
            failWithError(err, { announceOnConnect: info.announceOnConnect });
        }
    }, [connect, failWithError]);
    const joinOngoingCall = useCallback(async () => {
        const info = ongoingCall;
        if (!info || callStateRef.current !== "idle")
            return;
        rejoinInfoRef.current = null;
        setCanRejoin(false);
        clearStoredRejoin();
        setOngoingCall(null);
        setCallIdBoth(info.callId);
        setVideoBoth(info.video);
        setStateBoth("calling");
        let attemptSeq = connectSeqRef.current;
        try {
            await preflightBrowserMediaPermissions(info.video, facingRef.current);
            if (attemptSeq !== connectSeqRef.current || currentCallState() !== "calling")
                return;
            explicitMediaPermissionRef.current = true;
            const connectPromise = connect(info.video, info.callId);
            attemptSeq = connectSeqRef.current;
            await connectPromise;
        }
        catch (err) {
            if (attemptSeq !== connectSeqRef.current || currentCallState() === "idle")
                return;
            failWithError(err);
        }
    }, [ongoingCall, connect, failWithError]);
    const dismissCallDropped = useCallback(() => {
        setCallDropped(false);
        setCallDroppedByBackground(false);
    }, []);
    const dismissCallError = useCallback(() => setCallError(null), []);
    const shareFile = useCallback((file, mode = "download") => {
        const room = roomRef.current;
        if (!room || callStateRef.current !== "in-call")
            return false;
        if (file.size > MAX_FILE_SHARE_BYTES)
            return false;
        if (sendingFileRef.current)
            return false; // one outgoing transfer at a time
        const recipients = [];
        room.remoteParticipants.forEach((p) => {
            if (!canReceiveFileRef.current || canReceiveFileRef.current(p.identity))
                recipients.push(p.identity);
        });
        sendingFileRef.current = true;
        sendFileOverLiveKit(room, file, recipients, upsertTransfer, mode).finally(() => {
            sendingFileRef.current = false;
        });
        return true;
    }, [upsertTransfer]);
    const dismissTransfer = useCallback((id) => {
        setFileTransfers((prev) => {
            const tr = prev.find((t) => t.id === id);
            if (tr?.blobUrl)
                URL.revokeObjectURL(tr.blobUrl);
            return prev.filter((t) => t.id !== id);
        });
    }, []);
    /** Chat-WS signaling: ring + ongoing-call discovery. */
    const handleSignal = useCallback((signal) => {
        if (!enabled)
            return;
        const { subtype, callId: sigCallId, from, payload } = signal;
        switch (subtype) {
            case "lk-invite":
                if (callStateRef.current === "idle") {
                    rejoinInfoRef.current = null;
                    setCanRejoin(false);
                    clearStoredRejoin();
                    setCallIdBoth(sigCallId);
                    setIncomingFrom(from);
                    incomingVideoRef.current = !!payload?.video;
                    setIncomingVideo(!!payload?.video);
                    setStateBoth("incoming");
                }
                break;
            case "lk-reject":
                // a callee declined; if we're still alone in the room, end the attempt
                if (callStateRef.current === "calling" &&
                    sigCallId === callIdRef.current &&
                    (roomRef.current?.remoteParticipants.size ?? 0) === 0) {
                    teardown();
                }
                break;
            case "lk-beacon":
                if (callStateRef.current === "idle") {
                    const saved = rejoinInfoRef.current;
                    if (saved && saved.callId !== sigCallId) {
                        rejoinInfoRef.current = null;
                        setCanRejoin(false);
                        clearStoredRejoin();
                    }
                    ongoingSeenAtRef.current = Date.now();
                    setOngoingCall({ callId: sigCallId, video: !!payload?.video });
                }
                else if (callStateRef.current === "incoming" && sigCallId !== callIdRef.current) {
                    // the call we're being invited to may have been replaced — ignore
                }
                break;
            case "lk-screen-share-state": {
                const nextOwner = typeof payload?.ownerDeviceId === "string"
                    ? payload.ownerDeviceId
                    : null;
                const localWasOwner = screenShareOwnerRef.current === deviceIdRef.current;
                screenShareOwnerByCallRef.current.set(sigCallId, nextOwner);
                if (sigCallId !== callIdRef.current)
                    break;
                screenShareOwnerRef.current = nextOwner;
                setScreenShareOwnerId(nextOwner);
                syncParticipants();
                if (screenShareClaimPendingRef.current) {
                    if (screenShareClaimTimerRef.current) {
                        clearTimeout(screenShareClaimTimerRef.current);
                        screenShareClaimTimerRef.current = null;
                    }
                    screenShareClaimPendingRef.current = false;
                    setScreenShareClaimPending(false);
                    if (nextOwner === deviceIdRef.current) {
                        void startScreenShareAfterClaimRef.current?.();
                    }
                }
                // The relay remains authoritative if ownership is revoked while
                // this browser is still publishing. Unpublish/restore immediately;
                // releaseScreenShareOwnership will not clear the new remote owner.
                if (localWasOwner &&
                    nextOwner !== deviceIdRef.current &&
                    isScreenSharingRef.current) {
                    void stopLocalScreenShareRef.current?.();
                }
                break;
            }
            case "lk-screen-share-claim-result":
                if (sigCallId === callIdRef.current && payload?.granted === false) {
                    if (screenShareClaimTimerRef.current) {
                        clearTimeout(screenShareClaimTimerRef.current);
                        screenShareClaimTimerRef.current = null;
                    }
                    screenShareClaimPendingRef.current = false;
                    setScreenShareClaimPending(false);
                }
                break;
            case "lk-mute-one": // selective: only the addressed target complies
            case "lk-mute-request":
                // the host asked everyone (or just us, when addressed) to mute —
                // mute OURSELVES; a `to` addressed to someone else is ignored
                if (signal.subtype === "lk-mute-one" && signal.to !== deviceId)
                    break;
                if (signal.to && signal.to !== deviceId)
                    break;
                if (callStateRef.current === "in-call" &&
                    sigCallId === callIdRef.current &&
                    !isMutedRef.current) {
                    // Host-caused mute: only this may be undone by lk-unmute-request.
                    console.debug(`[livekit] lk-mute-request from ${from} — muting self (host-caused)`);
                    mutedByHostRef.current = true;
                    isMutedRef.current = true;
                    setIsMuted(true);
                    roomRef.current?.localParticipant.setMicrophoneEnabled(false).catch(() => { });
                }
                break;
            case "lk-unmute-one": // selective: only the addressed target complies
            case "lk-unmute-request":
                if (signal.subtype === "lk-unmute-one" && signal.to !== deviceId)
                    break;
                // The host withdrew its mute request (broadcast) or selectively
                // unmuted us (`to` = our deviceId). Comply ONLY if our mute was
                // caused by the host — a manual mute is never overridden.
                if (signal.to && signal.to !== deviceId)
                    break;
                console.debug(`[livekit] lk-unmute-request received from ${from}: state=${callStateRef.current}, callIdMatch=${sigCallId === callIdRef.current}, mutedByHost=${mutedByHostRef.current}, isMuted=${isMutedRef.current}`);
                if (callStateRef.current === "in-call" &&
                    sigCallId === callIdRef.current &&
                    mutedByHostRef.current &&
                    isMutedRef.current) {
                    mutedByHostRef.current = false;
                    isMutedRef.current = false;
                    setIsMuted(false);
                    roomRef.current?.localParticipant.setMicrophoneEnabled(true).catch(() => { });
                    // Being unmuted by the host fulfils a pending hand raise.
                    if (handRaisedRef.current && callIdRef.current) {
                        handRaisedRef.current = false;
                        setHandRaised(false);
                        sendRef.current({ subtype: "lk-hand", callId: callIdRef.current, payload: { raised: false } });
                    }
                }
                break;
            case "lk-hand":
                // a participant raised/lowered their hand — pure UI state
                if (callStateRef.current === "in-call" && sigCallId === callIdRef.current && from) {
                    setRaisedHands((prev) => signal.payload?.raised
                        ? prev.includes(from) ? prev : [...prev, from]
                        : prev.filter((id) => id !== from));
                }
                break;
            case "lk-kick":
                // the host removed us from the call — only the addressed target acts
                if (callStateRef.current === "in-call" &&
                    sigCallId === callIdRef.current &&
                    signal.to === deviceId) {
                    console.warn(`[livekit] removed from call by ${from} (host kick)`);
                    teardown();
                    setCallDroppedByBackground(false);
                    setCallDropped(true);
                }
                break;
            case "lk-probe":
                if ((callStateRef.current === "calling" || callStateRef.current === "in-call") &&
                    callIdRef.current &&
                    roomRef.current) {
                    sendRef.current({
                        subtype: "lk-beacon",
                        callId: callIdRef.current,
                        to: from,
                        payload: { video: isVideoCallRef.current },
                    });
                }
                break;
            default:
                break;
        }
    }, [enabled, syncParticipants, teardown]);
    // idle devices: probe for an ongoing call when (re)connecting to the room WS
    useEffect(() => {
        if (enabled && isConnected && callStateRef.current === "idle") {
            sendRef.current({ subtype: "lk-probe", callId: "probe" });
        }
    }, [enabled, isConnected]);
    // Permission can be granted from the browser/site settings after a denied
    // call attempt. The provider deliberately survives route changes, so that
    // old sentinel would otherwise keep showing over the chat forever. Observe
    // the permission state without opening media or triggering another prompt.
    useEffect(() => {
        if (callError !== MEDIA_DENIED_ERROR)
            return;
        const query = globalThis.navigator?.permissions?.query;
        if (!query)
            return;
        let active = true;
        let permission = null;
        const clearIfGranted = () => {
            if (!active || permission?.state !== "granted" || callStateRef.current !== "idle")
                return;
            setCallError((current) => current === MEDIA_DENIED_ERROR ? null : current);
        };
        void query.call(navigator.permissions, { name: "microphone" }).then((status) => {
            if (!active)
                return;
            permission = status;
            clearIfGranted();
            status.addEventListener?.("change", clearIfGranted);
        }).catch(() => {
            // Browsers may omit microphone permission introspection. Keep the
            // retryable error in that case instead of assuming access.
        });
        return () => {
            active = false;
            permission?.removeEventListener?.("change", clearIfGranted);
        };
    }, [callError]);
    // Retroactive E2EE upgrade: the chat v2 key arrived while a call is
    // already running unencrypted (guest joined before the ECDH handoff
    // finished / wallet user signed the key message only mid-call). Set the
    // key on the room's existing provider and enable frame E2EE on the live
    // room — e2eeActive and the ⚠️ indicator follow via updateE2eeStatus.
    useEffect(() => {
        if (!e2eeKeyAvailable)
            return;
        const room = roomRef.current;
        if (!room || !e2eePendingKeyRef.current || !e2eeKeyProviderRef.current)
            return;
        if (e2eeUpgradeInFlightRef.current)
            return;
        const getKey = getE2EEKeyMaterialRef.current;
        if (!getKey)
            return;
        e2eeUpgradeInFlightRef.current = true;
        (async () => {
            try {
                const keyBytes = await getKey();
                // key may have vanished / call may have ended while awaiting
                if (!keyBytes || roomRef.current !== room || !e2eeKeyProviderRef.current)
                    return;
                await setCallE2EEKey(e2eeKeyProviderRef.current, keyBytes);
                await room.setE2EEEnabled(true);
                if (roomRef.current !== room)
                    return;
                e2eePendingKeyRef.current = false;
                e2eeLocalRef.current = true;
                updateE2eeStatus(room);
                console.log("[lkCall] frame E2EE enabled mid-call (key arrived late)");
            }
            catch (err) {
                console.warn("[lkCall] mid-call E2EE upgrade failed:", err);
            }
            finally {
                e2eeUpgradeInFlightRef.current = false;
            }
        })();
    }, [e2eeKeyAvailable, callState, updateE2eeStatus]);
    // expire stale ongoing-call info
    useEffect(() => {
        const t = setInterval(() => {
            if (ongoingSeenAtRef.current && Date.now() - ongoingSeenAtRef.current > ONGOING_TTL_MS) {
                ongoingSeenAtRef.current = 0;
                setOngoingCall(null);
            }
        }, ONGOING_CHECK_MS);
        return () => clearInterval(t);
    }, []);
    // A mounted provider can move to another wallet room without unmounting.
    // Fully leave the old room instead of letting React run the unmount cleanup
    // against a changed dependency and leaving call state/media half alive.
    useEffect(() => {
        const previousRoomName = previousRoomNameRef.current;
        previousRoomNameRef.current = roomName;
        if (previousRoomName === roomName)
            return;
        teardown();
    }, [roomName, teardown]);
    // Restore a dropped/in-progress LiveKit call after refresh, Safari process
    // suspension, or any other hook remount. The server/session remains the
    // authority: a stale room simply fails to reconnect instead of silently
    // hiding the only recovery action after an arbitrary local timeout.
    useEffect(() => {
        if (!enabled || callStateRef.current !== "idle")
            return;
        const info = loadStoredRejoin(roomName);
        if (!info)
            return;
        rejoinInfoRef.current = info;
        setCanRejoin(true);
    }, [enabled, roomName]);
    // full cleanup on unmount
    useEffect(() => () => {
        if (callStateRef.current !== "idle" && callIdRef.current) {
            const info = {
                callId: callIdRef.current,
                video: isVideoCallRef.current,
                ts: Date.now(),
                forceRelay: relayModeRef.current,
                muted: isMutedRef.current,
                mutedByHost: mutedByHostRef.current,
                cameraOff: isCameraOffRef.current,
            };
            rejoinInfoRef.current = info;
            saveStoredRejoin(activeRoomNameRef.current, info);
        }
        if (screenShareOwnerRef.current === deviceIdRef.current && callIdRef.current) {
            sendRef.current({ subtype: "lk-screen-share-release", callId: callIdRef.current });
        }
        if (screenShareClaimTimerRef.current) {
            clearTimeout(screenShareClaimTimerRef.current);
            screenShareClaimTimerRef.current = null;
        }
        connectSeqRef.current += 1;
        recoveryGenerationRef.current += 1;
        screenShareGenerationRef.current += 1;
        screenShareTransitionOperationRef.current += 1;
        screenShareTransitionRef.current = false;
        screenShareEndedDuringTransitionRef.current = false;
        screenShareStopInProgressRef.current = false;
        recoveryInFlightRef.current = false;
        if (beaconTimerRef.current)
            clearInterval(beaconTimerRef.current);
        clearRecoveryTimers();
        if (e2eeWorkerRef.current) {
            e2eeWorkerRef.current.terminate();
            e2eeWorkerRef.current = null;
        }
        const room = roomRef.current;
        roomRef.current = null;
        if (room) {
            room.removeAllListeners();
            try {
                for (const pub of room.localParticipant.trackPublications.values()) {
                    pub.track?.mediaStreamTrack?.stop();
                    pub.track?.stop();
                }
            }
            catch {
                /* best-effort — disconnect below is the primary cleanup */
            }
            room.disconnect().catch(() => { });
        }
    }, [clearRecoveryTimers]);
    return {
        connectionPhase,
        callState,
        participants,
        incomingFrom,
        callId,
        isMuted,
        isVideoCall,
        incomingVideo,
        isCameraOff,
        localStream,
        startCall,
        acceptCall,
        rejectCall,
        hangup,
        leaveCall,
        toggleMute,
        muteAll,
        unmuteAll,
        muteAllActive,
        setParticipantMuted,
        handRaised,
        toggleHand,
        raisedHands,
        kickParticipant,
        toggleCamera,
        switchCamera,
        switchMicrophone,
        isScreenSharing,
        screenShareDisabled: !isConnected ||
            screenShareClaimPending ||
            (!!screenShareOwnerId && screenShareOwnerId !== deviceId),
        toggleScreenShare,
        canRejoin,
        /** Stable retained recovery identity. Unlike live `callId`, this remains
         * available after teardown and changes whenever recovery info is replaced. */
        rejoinCallIdentity: rejoinInfoRef.current
            ? `livekit:${rejoinInfoRef.current.callId}:${rejoinInfoRef.current.ts}`
            : null,
        rejoinCallId: rejoinInfoRef.current?.callId ?? null,
        rejoinCall,
        callDroppedByBackground,
        ongoingCall,
        joinOngoingCall,
        callDropped,
        dismissCallDropped,
        handleSignal,
        fileTransfers,
        shareFile,
        dismissTransfer,
        callError,
        dismissCallError,
        connectionRecovery,
        e2eeActive,
        e2eeSupported: callE2EESupported(),
    };
}
